import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ticket, Send, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { CONTACT } from "../../data/content";
import { getMailAcces } from "./EmailGate";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const EMPTY = {
  prenom: "",
  nom: "",
  tel: "",
  email: "",
  par_structure: false,
  structure_nom: "",
  attestation: false,
  cgu: false,
};

const ATTESTATION_TEXT =
  "J'atteste sur l'honneur traverser un moment de vie où j'ai besoin de répit ou de ressourcement, ne présenter aucune contre-indication médicale à la pratique de l'expérience choisie et que les informations données sont exactes.";

const SectionTitle = ({ children }) => (
  <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-pblv-mauve">{children}</p>
);

export function CodeForm({ onDone, offre = "", besoin = "", partenaire = "", partenaireTel = "", avantage = "", delaiReservation = "" }) {
  const storedMail = getMailAcces()?.email || "";
  const [form, setForm] = useState({ ...EMPTY, email: storedMail });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const up = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const ready = form.attestation && form.cgu;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Indiquez votre adresse e-mail : votre code vous sera envoyé par mail.");
      return;
    }
    if (form.par_structure && !form.structure_nom.trim()) {
      toast.error("Indiquez le nom de la structure.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/code-demandes", {
        ...form,
        canal: "mail",
        offre,
        besoin,
        partenaire,
        partenaire_tel: partenaireTel,
        avantage,
        delai_reservation: delaiReservation,
      });
      setForm({ ...EMPTY, email: storedMail });
      setSent(true);
    } catch (err) {
      toast.error("Envoi impossible", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="code-success" className="flex flex-col items-center gap-4 py-6 text-center">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
          <Check className="h-7 w-7" strokeWidth={2} />
        </span>
        <h3 className="font-serif text-2xl font-medium text-pblv-ink">Votre demande est bien reçue.</h3>
        <p className="max-w-sm text-sm leading-relaxed text-pblv-inksoft">
          Vous recevrez votre code par e-mail et par SMS après validation.
        </p>
        {onDone && (
          <button
            onClick={onDone}
            data-testid="code-close"
            className="mt-2 rounded-full border border-pblv-mauve/40 px-7 py-3 text-sm font-semibold text-pblv-mauve transition-colors hover:bg-pblv-mauve/5"
          >
            Fermer
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      {offre && (
        <div data-testid="code-offre" className="rounded-xl bg-pblv-mauve/10 px-4 py-3 text-sm">
          <p className="font-semibold text-pblv-ink">
            Votre T'POZ : <span className="text-pblv-mauve">{offre}</span>
          </p>
          {besoin && <p className="mt-0.5 text-xs text-pblv-inksoft">Besoin : {besoin}</p>}
        </div>
      )}

      <label className="flex items-start gap-3 rounded-xl bg-pblv-mauve/5 p-3 text-sm text-pblv-ink">
        <input
          type="checkbox"
          data-testid="code-par-structure"
          checked={form.par_structure}
          onChange={up("par_structure")}
          className="mt-0.5 h-4 w-4 shrink-0 accent-pblv-mauve"
        />
        Je fais la demande pour une femme que j'accompagne
      </label>
      {form.par_structure && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="c-structure">Nom de la structure *</Label>
          <Input
            id="c-structure"
            data-testid="code-structure"
            value={form.structure_nom}
            onChange={up("structure_nom")}
            placeholder="Ex. : CCAS de Petit-Bourg"
          />
          <p className="text-xs text-pblv-inksoft">
            Renseignez ci-dessous les coordonnées de la bénéficiaire.
          </p>
        </div>
      )}

      <SectionTitle>{form.par_structure ? "Coordonnées de la bénéficiaire" : "Vos coordonnées"}</SectionTitle>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="c-prenom">Prénom *</Label>
          <Input id="c-prenom" data-testid="code-prenom" required value={form.prenom} onChange={up("prenom")} placeholder="Prénom" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="c-nom">Nom *</Label>
          <Input id="c-nom" data-testid="code-nom" required value={form.nom} onChange={up("nom")} placeholder="Nom" />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="c-tel">Téléphone *</Label>
        <Input id="c-tel" data-testid="code-tel" required value={form.tel} onChange={up("tel")} placeholder="0690 …" />
      </div>
      {storedMail && !form.par_structure ? (
        <p data-testid="code-email-rappel" className="flex items-center gap-2 rounded-xl bg-white px-3 py-2.5 text-sm text-pblv-ink">
          <Mail className="h-4 w-4 shrink-0 text-pblv-mauve" />
          Votre code sera envoyé à : <span className="font-semibold">{storedMail}</span>
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="c-email">E-mail *</Label>
          <Input
            id="c-email"
            type="email"
            data-testid="code-email"
            required
            value={form.email}
            onChange={up("email")}
            placeholder="vous@exemple.com"
          />
          <p className="text-xs text-pblv-inksoft">Votre code vous sera envoyé à cette adresse.</p>
        </div>
      )}

      <label className="flex items-start gap-3 rounded-xl bg-white p-3 text-sm text-pblv-ink">
        <input
          type="checkbox"
          data-testid="code-attestation"
          checked={form.attestation}
          onChange={up("attestation")}
          className="mt-0.5 h-4 w-4 shrink-0 accent-pblv-mauve"
        />
        {ATTESTATION_TEXT}
      </label>
      <label className="flex items-start gap-3 rounded-xl bg-white p-3 text-sm text-pblv-ink">
        <input
          type="checkbox"
          data-testid="code-cgu"
          checked={form.cgu}
          onChange={up("cgu")}
          className="mt-0.5 h-4 w-4 shrink-0 accent-pblv-mauve"
        />
        <span>
          J'accepte les{" "}
          <Link to="/conditions" target="_blank" data-testid="code-cgu-lien" className="font-semibold text-pblv-mauve underline underline-offset-2">
            conditions d'utilisation de T'POZ
          </Link>
          . *
        </span>
      </label>

      <button
        type="submit"
        data-testid="code-submit"
        disabled={submitting || !ready}
        className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-7 py-4 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark disabled:opacity-50"
      >
        <Send className="h-4 w-4" strokeWidth={1.8} />
        {submitting ? "Envoi en cours…" : "Demander mon code"}
      </button>
      <p className="text-center text-sm text-pblv-inksoft">
        Vous préférez appeler ?{" "}
        <a href={CONTACT.phoneHref} data-testid="code-appel-assoc" className="font-semibold text-pblv-mauve underline underline-offset-4">
          {CONTACT.phone}
        </a>
      </p>
    </form>
  );
}

export function DemandeDialog({ open, onOpenChange, offre = "", besoin = "", partenaire = "", partenaireTel = "", avantage = "", delaiReservation = "" }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        data-testid="demande-dialog"
        className="max-h-[90vh] overflow-y-auto rounded-3xl bg-pblv-bg sm:max-w-xl"
      >
        <DialogHeader>
          <span className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-pblv-mauve/12 text-pblv-mauve">
            <Ticket className="h-6 w-6" strokeWidth={1.6} />
          </span>
          <DialogTitle className="font-serif text-3xl font-medium text-pblv-ink">
            Obtenir mon code pour réserver
          </DialogTitle>
          <DialogDescription className="text-pblv-inksoft">
            Faites votre demande en quelques minutes. Après validation par T'POZ, vous recevrez
            votre code par mail — valable pour 3 T'POZ par an.
          </DialogDescription>
        </DialogHeader>
        <CodeForm offre={offre} besoin={besoin} partenaire={partenaire} partenaireTel={partenaireTel} avantage={avantage} delaiReservation={delaiReservation} onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

export const DemandeBon = () => {
  const [open, setOpen] = useState(false);
  const steps = [
    "Je découvre les T'POZ",
    "Je demande mon code",
    "Je réserve auprès du partenaire avec mon code",
  ];

  return (
    <section id="demande" data-testid="demande-section" className="relative bg-pblv-bg py-20 md:py-28">
      <div className="mx-auto max-w-[1400px] px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-[2.5rem] bg-pblv-ink px-6 py-14 text-pblv-bg md:px-16 md:py-20"
        >
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-pblv-mauve/30 blur-[110px]" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-pblv-sage/20 blur-[110px]" />
          <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-pblv-rose">
                Demander une T'POZ
              </p>
              <h2 className="font-serif text-4xl font-medium leading-[1.02] md:text-6xl">
                Obtenez votre
                <br />
                <span className="italic text-pblv-rose">code T'POZ.</span>
              </h2>
              <p className="mt-5 max-w-lg text-lg leading-relaxed text-pblv-bg/70">
                Un code personnel, valable pour 3 T'POZ par an, au choix dans toute la sélection.
                Faites votre demande en quelques minutes.
              </p>
              <ul className="mt-6 flex flex-col gap-2">
                {steps.map((s, i) => (
                  <li key={s} className="flex items-center gap-3 text-pblv-bg/90">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-pblv-rose/25 text-xs font-bold text-pblv-rose">
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-3 lg:col-span-4 lg:col-start-9">
              <button
                onClick={() => setOpen(true)}
                data-testid="open-demande-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-pblv-bg px-7 py-4 text-sm font-semibold text-pblv-ink transition-all duration-300 hover:-translate-y-1 hover:bg-pblv-rose"
              >
                <Ticket className="h-4 w-4" strokeWidth={1.8} /> Demander ma T'POZ
              </button>
              <p className="mt-1 flex items-center justify-center gap-2 text-xs text-pblv-bg/50">
                <Check className="h-3.5 w-3.5" /> Vous préférez appeler ?{" "}
                <a href={CONTACT.phoneHref} className="font-semibold text-pblv-bg/80 underline underline-offset-4">{CONTACT.phone}</a>
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <DemandeDialog open={open} onOpenChange={setOpen} />
    </section>
  );
};

export default DemandeBon;
